import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Copy, GitBranch, MessageSquare, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar } from "@/components/User/Avatar";
import { Prompt } from "@/lib/types";
import { CommentSection } from "@/components/Comments/CommentSection";
import { NeonUpvoteIcon } from "@/components/ui/NeonUpvoteIcon";
import { formatDistanceToNow } from "date-fns";
import { useAuth } from "@/context/AuthContext";
import { usePromptActions } from "@/hooks/prompts/use-prompt-actions";
import { supabase } from "@/integrations/supabase/client";
import { toDatabaseValue } from "@/utils/supabase/type-guards";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

interface PromptDetailModalProps {
  prompt: Prompt | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdate?: (id: string, updates: Partial<Prompt>) => void;
}

export function PromptDetailModal({
  prompt,
  isOpen,
  onClose,
  onUpdate,
}: PromptDetailModalProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [commentCount, setCommentCount] = useState(prompt?.comments || 0);
  const [copyCount, setCopyCount] = useState(prompt?.copyCount || 0);

  const {
    isLiked,
    likeCount,
    handleLike,
  } = usePromptActions(prompt, user, onUpdate);

  // Handle browser back button - push state when modal opens, listen for popstate
  useEffect(() => {
    if (isOpen) {
      // Push a state when modal opens
      window.history.pushState({ modal: true }, "");
      
      const handlePopState = () => {
        // When back is pressed, close the modal
        onClose();
      };
      
      window.addEventListener("popstate", handlePopState);
      
      return () => {
        window.removeEventListener("popstate", handlePopState);
      };
    }
  }, [isOpen, onClose]);

  // Handle manual close (X button) - go back in history
  const handleClose = () => {
    window.history.back();
  };

  const handleCopy = async () => {
    if (!prompt) return;
    try {
      await navigator.clipboard.writeText(prompt.content);
      await supabase.rpc("increment_copy_count", {
        prompt_id: toDatabaseValue(prompt.id),
      });
      setCopyCount((prev) => prev + 1);
      toast.success("Prompt copied to clipboard!");
    } catch (error) {
      toast.error("Failed to copy prompt");
    }
  };

  const handleUserClick = () => {
    if (prompt) {
      handleClose();
      setTimeout(() => navigate(`/users/${prompt.userId}`), 50);
    }
  };

  if (!prompt) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className={cn(
              "fixed z-50 overflow-hidden",
              "bg-black/80 backdrop-blur-lg",
              "border border-white/10 rounded-2xl",
              "shadow-[0_0_50px_rgba(0,194,255,0.1)]",
              isMobile
                ? "inset-0 rounded-none"
                : "left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-2xl max-h-[90vh]"
            )}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <div className="flex items-center gap-3">
                {/* Back button for mobile */}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleClose}
                  className="text-white/60 hover:text-white hover:bg-white/10 mr-1"
                >
                  <ArrowLeft className="h-5 w-5" />
                </Button>
                <div
                  className="flex items-center gap-3 cursor-pointer"
                  onClick={handleUserClick}
                >
                  <Avatar
                    userId={prompt.userId}
                    avatarId={prompt.avatarId}
                    size="md"
                  />
                  <div>
                    <h4 className="font-semibold text-white hover:text-brand-cyan transition-colors">
                      @{prompt.username}
                    </h4>
                    <span className="text-xs text-white/40">
                      {formatDistanceToNow(new Date(prompt.createdAt), {
                        addSuffix: true,
                      })}
                    </span>
                  </div>
                </div>
              </div>
              {!isMobile && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleClose}
                  className="text-white/60 hover:text-white hover:bg-white/10"
                >
                  <X className="h-5 w-5" />
                </Button>
              )}
            </div>

            {/* Content */}
            <div
              className={cn(
                "overflow-y-auto",
                isMobile ? "h-[calc(100vh-180px)]" : "max-h-[calc(90vh-140px)]"
              )}
            >
              {/* Prompt Content */}
              <div className="p-4 border-b border-brand-cyan/10">
                {/* Category & Remix Badge */}
                <div className="flex items-center gap-2 mb-3">
                  <Badge className="bg-brand-cyan/20 text-brand-cyan border-brand-cyan/30 text-xs">
                    #{prompt.category}
                  </Badge>
                  {prompt.isRemix && (
                    <Badge className="bg-brand-pink/20 text-brand-pink border-brand-pink/30 text-xs">
                      <GitBranch className="h-3 w-3 mr-1" />
                      Remix
                    </Badge>
                  )}
                </div>

                {/* Title */}
                {prompt.title && (
                  <h2 className="text-xl font-bold text-white mb-3">
                    {prompt.title}
                  </h2>
                )}

                {/* Content */}
                <div className="text-white/80 leading-relaxed whitespace-pre-wrap mb-4">
                  {prompt.content}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-4 pt-3 border-t border-white/10">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleLike(!isLiked)}
                    className="text-white/60 hover:text-brand-cyan"
                  >
                    <NeonUpvoteIcon
                      className="h-5 w-5 mr-1.5"
                      isActive={isLiked}
                    />
                    <span className={isLiked ? "text-brand-cyan" : ""}>
                      {likeCount}
                    </span>
                  </Button>

                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-white/60"
                  >
                    <MessageSquare className="h-4 w-4 mr-1.5" />
                    {commentCount}
                  </Button>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleCopy}
                    className="text-white/60 hover:text-green-400"
                  >
                    <Copy className="h-4 w-4 mr-1.5" />
                    {copyCount}
                  </Button>
                </div>
              </div>

              {/* Comments Section */}
              <CommentSection
                promptId={prompt.id}
                onNewComment={() => setCommentCount((prev) => prev + 1)}
              />
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
